'use client';
import { _ as _object_without_properties_loose, a as _extends } from './cc-CWi2JB6O.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { cn } from '@repo/lib/utils';
import { Button } from './button.js';
import { ArrowLeftIcon, ArrowRightIcon } from '@radix-ui/react-icons';

const CarouselContext = /*#__PURE__*/ React.createContext(null);
function useCarousel() {
    const context = React.useContext(CarouselContext);
    if (!context) {
        throw new Error("useCarousel must be used within a <Carousel />");
    }
    return context;
}
function Carousel(_0) {
    let { orientation = "horizontal", opts, setApi, plugins, className, children } = _0, props = _object_without_properties_loose(_0, [
        "orientation",
        "opts",
        "setApi",
        "plugins",
        "className",
        "children"
    ]);
    const [carouselRef, api] = useEmblaCarousel(_extends({}, opts, {
        axis: orientation === "horizontal" ? "x" : "y"
    }), plugins);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const onSelect = React.useCallback((api)=>{
        if (!api) return;
        setCanScrollPrev(api.canScrollPrev());
        setCanScrollNext(api.canScrollNext());
    }, []);
    const scrollPrev = React.useCallback(()=>{
        api == null ? void 0 : api.scrollPrev();
    }, [
        api
    ]);
    const scrollNext = React.useCallback(()=>{
        api == null ? void 0 : api.scrollNext();
    }, [
        api
    ]);
    const handleKeyDown = React.useCallback((event)=>{
        if (event.key === "ArrowLeft") {
            event.preventDefault();
            scrollPrev();
        } else if (event.key === "ArrowRight") {
            event.preventDefault();
            scrollNext();
        }
    }, [
        scrollPrev,
        scrollNext
    ]);
    React.useEffect(()=>{
        if (!api || !setApi) return;
        setApi(api);
    }, [
        api,
        setApi
    ]);
    React.useEffect(()=>{
        if (!api) return;
        onSelect(api);
        api.on("reInit", onSelect);
        api.on("select", onSelect);
        return ()=>{
            api == null ? void 0 : api.off("select", onSelect);
        };
    }, [
        api,
        onSelect
    ]);
    return /*#__PURE__*/ jsx(CarouselContext.Provider, {
        value: {
            carouselRef,
            api: api,
            opts,
            orientation: orientation || ((opts == null ? void 0 : opts.axis) === "y" ? "vertical" : "horizontal"),
            scrollPrev,
            scrollNext,
            canScrollPrev,
            canScrollNext
        },
        children: /*#__PURE__*/ jsx("div", _extends({
            onKeyDownCapture: handleKeyDown,
            className: cn("relative", className),
            role: "region",
            "aria-roledescription": "carousel",
            "data-slot": "carousel"
        }, props, {
            children: children
        }))
    });
}
function CarouselContent(_0) {
    let { className } = _0, props = _object_without_properties_loose(_0, [
        "className"
    ]);
    const { carouselRef, orientation } = useCarousel();
    return /*#__PURE__*/ jsx("div", {
        ref: carouselRef,
        className: "overflow-hidden",
        "data-slot": "carousel-content",
        children: /*#__PURE__*/ jsx("div", _extends({
            className: cn("flex", orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col", className)
        }, props))
    });
}
function CarouselItem(_0) {
    let { className } = _0, props = _object_without_properties_loose(_0, [
        "className"
    ]);
    const { orientation } = useCarousel();
    return /*#__PURE__*/ jsx("div", _extends({
        role: "group",
        "aria-roledescription": "slide",
        "data-slot": "carousel-item",
        className: cn("min-w-0 shrink-0 grow-0 basis-full", orientation === "horizontal" ? "pl-4" : "pt-4", className)
    }, props));
}
function CarouselPrevious(_0) {
    let { className, variant = "outline", size = "icon" } = _0, props = _object_without_properties_loose(_0, [
        "className",
        "variant",
        "size"
    ]);
    const { orientation, scrollPrev, canScrollPrev } = useCarousel();
    return /*#__PURE__*/ jsxs(Button, _extends({
        "data-slot": "carousel-previous",
        variant: variant,
        size: size,
        className: cn("absolute size-8 rounded-full", orientation === "horizontal" ? "top-1/2 left-4 -translate-y-1/2" : "-top-12 left-1/2 -translate-x-1/2 rotate-90", className),
        disabled: !canScrollPrev,
        onClick: scrollPrev
    }, props, {
        children: [
            /*#__PURE__*/ jsx(ArrowLeftIcon, {}),
            /*#__PURE__*/ jsx("span", {
                className: "sr-only",
                children: "Previous slide"
            })
        ]
    }));
}
function CarouselNext(_0) {
    let { className, variant = "outline", size = "icon" } = _0, props = _object_without_properties_loose(_0, [
        "className",
        "variant",
        "size"
    ]);
    const { orientation, scrollNext, canScrollNext } = useCarousel();
    return /*#__PURE__*/ jsxs(Button, _extends({
        "data-slot": "carousel-next",
        variant: variant,
        size: size,
        className: cn("absolute size-8 rounded-full", orientation === "horizontal" ? "top-1/2 right-4 -translate-y-1/2" : "-bottom-12 left-1/2 -translate-x-1/2 rotate-90", className),
        disabled: !canScrollNext,
        onClick: scrollNext
    }, props, {
        children: [
            /*#__PURE__*/ jsx(ArrowRightIcon, {}),
            /*#__PURE__*/ jsx("span", {
                className: "sr-only",
                children: "Next slide"
            })
        ]
    }));
}

export { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious };
